// src/components/TrailerInventoryModal.js
import React, { useEffect, useState } from "react";
import "../styles/TrailerInventoryModal.css";
import { useTranslation } from "react-i18next";

export default function TrailerInventoryModal({
  isOpen,
  trailer, // { id, trailerPlate }
  initialInventory, // { numberOfStraps, numberOfRatchets, numberOfEdgeProtectors, numberOfAntiSlipMats, numberOfWoodenBeams, numberOfSidebars, numberOfFireExtinguishers }
  onClose,
  onSaveAndContinue, // (updatedInventory) => void
  onContinue, // () => void
  isSaving = false,
}) {
  const { t } = useTranslation("trailer_inventory_modal");

  const [values, setValues] = useState(initialInventory || {});

  useEffect(() => {
    setValues(initialInventory || {});
  }, [initialInventory]);

  if (!isOpen) return null;

  const setNum = (key) => (e) => {
    const v = e.target.value;
    const n = v === "" ? "" : Math.max(0, parseInt(v, 10) || 0);
    setValues((prev) => ({ ...prev, [key]: n }));
  };

  const subtitle = trailer?.trailerPlate
    ? t("subtitleWithPlate", { plate: trailer.trailerPlate })
    : t("subtitleNoPlate");

  return (
    <div className="trailerinventory-modal-overlay" role="dialog" aria-modal="true">
      <div className="trailerinventory-modal">
        {/* HEADER */}
        <div className="trailerinventory-modal-header">
          <div className="trailerinventory-modal-title-block">
            <div className="trailerinventory-modal-title">{t("title")}</div>
            <div className="trailerinventory-modal-subtitle">{subtitle}</div>
          </div>

          <button
            type="button"
            className="trailerinventory-close"
            onClick={onClose}
            disabled={isSaving}
            aria-label={t("aria.close")}
            title={t("aria.close")}
          >
            ×
          </button>
        </div>

        {/* BODY */}
        <div className="trailerinventory-modal-body">
          <p className="trailerinventory-intro">{t("intro")}</p>

          <div className="trailerinventory-fields">
            <div className="trailerinventory-field">
              <label>{t("fields.straps")}</label>
              <input
                type="number"
                min="0"
                value={values.numberOfStraps ?? 0}
                onChange={setNum("numberOfStraps")}
                aria-label={t("fields.straps")}
              />
            </div>

            <div className="trailerinventory-field">
              <label>{t("fields.ratchets")}</label>
              <input
                type="number"
                min="0"
                value={values.numberOfRatchets ?? 0}
                onChange={setNum("numberOfRatchets")}
                aria-label={t("fields.ratchets")}
              />
            </div>

            <div className="trailerinventory-field">
              <label>{t("fields.edgeProtectors")}</label>
              <input
                type="number"
                min="0"
                value={values.numberOfEdgeProtectors ?? 0}
                onChange={setNum("numberOfEdgeProtectors")}
                aria-label={t("fields.edgeProtectors")}
              />
            </div>

            <div className="trailerinventory-field">
              <label>{t("fields.antiSlipMats")}</label>
              <input
                type="number"
                min="0"
                value={values.numberOfAntiSlipMats ?? 0}
                onChange={setNum("numberOfAntiSlipMats")}
                aria-label={t("fields.antiSlipMats")}
              />
            </div>

            <div className="trailerinventory-field">
              <label>{t("fields.woodenBeams")}</label>
              <input
                type="number"
                min="0"
                value={values.numberOfWoodenBeams ?? 0}
                onChange={setNum("numberOfWoodenBeams")}
                aria-label={t("fields.woodenBeams")}
              />
            </div>

            <div className="trailerinventory-field">
              <label>{t("fields.sidebars")}</label>
              <input
                type="number"
                min="0"
                value={values.numberOfSidebars ?? 0}
                onChange={setNum("numberOfSidebars")}
                aria-label={t("fields.sidebars")}
              />
            </div>

            <div className="trailerinventory-field">
              <label>{t("fields.fireExtinguishers")}</label>
              <input
                type="number"
                min="0"
                value={values.numberOfFireExtinguishers ?? 0}
                onChange={setNum("numberOfFireExtinguishers")}
                aria-label={t("fields.fireExtinguishers")}
              />
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="trailerinventory-modal-footer">
          <button
            className="trailerinventory-btn trailerinventory-btn-secondary"
            onClick={onClose}
            disabled={isSaving}
            type="button"
          >
            {t("buttons.cancel")}
          </button>

          <button
            className="trailerinventory-btn trailerinventory-btn-ghost"
            onClick={onContinue}
            disabled={isSaving}
            type="button"
            title={t("buttons.continueNoChanges")}
            aria-label={t("buttons.continueNoChanges")}
          >
            {t("buttons.continueNoChanges")}
          </button>

          <button
            className="trailerinventory-btn trailerinventory-btn-primary"
            onClick={() => onSaveAndContinue(values)}
            disabled={isSaving}
            type="button"
            title={t("buttons.saveContinueTitle")}
            aria-label={t("buttons.saveContinueTitle")}
          >
            {isSaving ? t("buttons.saving") : t("buttons.saveContinue")}
          </button>
        </div>
      </div>
    </div>
  );
}
